<template>
    <section
        :class="{ opened }"
        class="lam-wps-level-container lam-wps-phase"
    >
        <div class="lam-wps-row lam-phase-header">
            <div class="lam-wps-cell column-actions">
                <a
                    href
                    class="h3 uk-icon-hover uk-icon-chevron-right"
                    @click.prevent="opened = !opened"
                ></a>
            </div>
            <div class="lam-wps-cell lam-phase-title">
                <phase-title
                    :record="record"
                    :editable="editable"
                    class="h3"
                >
                    <template v-slot:subtitle="{ title }">
                        <div class="uk-text-small">
                            <form-input
                                :id="`phase-${record.id}-deltek_id`"
                                name="deltek_id"
                                v-model="record.deltek_id"
                                :editable="is_db_admin_active || im_pic_or_pm"
                                :required="false"
                                size="small"
                                class="uk-display-inline-block"
                                :input-on-enter="true"
                                @input="save_field"
                                @click.native.stop
                            >
                                <template v-slot:read_only>
                                    {{ record.deltek_id }}
                                </template>
                            </form-input>
                            <div v-if="!!record.title" class="uk-margin-left uk-display-inline-block">{{ title }}</div>
                            <base-button
                                size="mini"
                                flavor="link"
                                title="Phase Comments"
                                :class="{ 'uk-invisible': !comments.length }"
                                class="uk-margin-left-small uk-float-right"
                                @click.prevent.stop="$emit('show-comments', record, comments)"
                            >
                                <open-icon glyph="comments" />
                            </base-button>
                            <base-button
                                v-if="completed_milestones"
                                flavor="link"
                                size="mini"
                                class="uk-float-right"
                                @click.prevent="show_completed =! show_completed"
                            >
                                (<span v-if="!show_completed">Show</span><span v-else>Hide</span> completed)
                            </base-button>
                        </div>
                    </template>
                </phase-title>
            </div>
            <div
                class="lam-wps-cell lam-phase-date-range  uk-flex-item-1 uk-text-center"
            >
                <h3>{{ record.computed_starts_at | nibnut.date("MMM YYYY") }} - {{ record.computed_ends_at | nibnut.date("MMM YYYY") }}</h3>
            </div>
            <div class="lam-wps-cell lam-phase-totals">
                <div class="uk-flex">
                    <div class="uk-flex-item-none">
                        <h3>$&nbsp;{{ record.fee | nibnut.number("0,0") }}</h3>
                        <div>Total</div>
                    </div>
                    <div class="uk-flex-item-none">
                        <h3>$&nbsp;{{ (record.fee - record.computed_spent) | nibnut.number("0,0") }}</h3>
                        <div>Remaining</div>
                    </div>
                    <div class="uk-flex-item-none">
                        <h3>$&nbsp;{{ phase_fee_projected(record) | nibnut.number("0,0") }}</h3>
                        <div>Projected</div>
                    </div>
                </div>
            </div>
        </div>
        <div class="lam-collapsible">
            <div
                v-if="visible_milestones.length"
                class="lam-wps-row lam-phase-staff"
            >
                <div class="lam-wps-cell column-actions">&nbsp;</div>
                <div class="lam-wps-cell column-title uk-flex-item-1">&nbsp;</div>
                <div class="lam-wps-cell column-trash">&nbsp;</div>
                <div
                    v-for="(user, index) in visible_team_members(hide_unstarred_team_members)"
                    :key="`${team_capacity_by_index(index)}-${user.id}`"
                    class="lam-wps-cell column-lammer"
                >
                    {{ team_member_capacity(user) }}<br />
                    <span>{{ user.initials }}</span>
                </div>
                <div class="lam-wps-cell column-total-hours-per-week uk-text-center">
                    <base-button
                        flavor="link"
                        size="mini"
                        :title="hide_unstarred_team_members ? 'Show unstarred' : 'Hide unstarred'"
                        @click.prevent="toggle_unstarred_team_members"
                    >
                        <open-icon :glyph="hide_unstarred_team_members ? 'star' : 'star-o'" />
                    </base-button>
                </div>
                <div class="lam-wps-cell column-date-range">
                    <div class="uk-grid uk-grid-small uk-grid-width-1-2">
                        <div class="uk-push-1-2">Due</div>
                    </div>
                </div>
                <div class="lam-wps-cell column-duration">&nbsp;</div>
                <div class="lam-wps-cell column-billing">
                    Billing
                </div>
            </div>
            <draggable
                group="milestones"
                :list="visible_milestones"
                :sort="true"
                handle=".lam-drag-handle"
                draggable=".lam-milestone"
                :disabled="!editable"
                class="lam-wps-row-children"
                @change="sorted"
            >
                <milestone
                    v-for="milestone in visible_milestones"
                    :key="milestone.id"
                    :record="milestone"
                    :phase-fee-projected="phase_fee_projected(record)"
                    :project="project"
                    :editable="editable"
                    :frozen="frozen_row_state"
                    :show-completed="show_completed"
                    :hide-unstarred="hide_unstarred_team_members"
                    :manual-sort="manual_sort"
                    :show-initials="false"
                    @toggled="reroot_ids"
                />
            </draggable>
            <div v-if="editable" class="uk-button-group uk-margin-top uk-margin-bottom">
                <base-button
                    flavor="link"
                    size="mini"
                    @click.prevent="milestone_act('add')"
                >
                    <open-icon glyph="plus" class="uk-margin-small-right" />
                    Task / Milestone
                </base-button>
                <div
                    :class="{ 'uk-open': milestone_menu_opened }"
                >
                    <base-button
                        flavor="link"
                        size="mini"
                        @click.prevent="toggle_milestones_menu"
                    >
                        <open-icon
                            glyph="caret-right"
                        />
                    </base-button>
                    <div class="uk-dropdown uk-dropdown-small">
                        <ul class="uk-nav uk-nav-dropdown">
                            <li
                                v-for="action in milestone_actions"
                                :key="action.id"
                            >
                                <a
                                    href="#"
                                    @click.prevent="milestone_act(action.id)"
                                >
                                    {{ action.name }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <tier-picker
            :show.sync="tier_picking"
            :phase-id="record.id"
            :phase-type-id="record.phase"
        />
    </section>
</template>

<script>
import { orderBy } from "lodash"

import handles_saving from "../../../mixins/HandlesSaving"
import profile_utilities from "../../../mixins/ProfileUtilities"
import phase_utilities from "../../../mixins/PhaseUtilities"
import handles_team_members from "./HandlesTeamMembers"

import PhaseTitle from "../ui/PhaseTitle"
import FormInput from "../../../ui/FormInput"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import Milestone from "./Milestone"
import TierPicker from "./TierPicker"

import draggable from "vuedraggable"

export default {
    mixins: [handles_saving, profile_utilities, phase_utilities, handles_team_members],
    components: {
        PhaseTitle,
        FormInput,
        BaseButton,
        OpenIcon,
        Milestone,
        TierPicker,
        draggable
    },
    mounted () {
        this.reroot()
    },
    watch: {
        "record.id": "reroot",
        show_completed: "reroot_ids"
    },
    methods: {
        reroot_ids () {
            const rows = orderBy(
                this.milestones.filter(task => {
                    return (!task.completed_at || (this.show_completed && !!task.completed_at))
                }
                ), Object.keys(this.manual_sort), Object.values(this.manual_sort))
            this.ordered_ids = rows.map(row => row.id)
        },
        reroot () {
            this.record_id = this.record ? this.record.id : 0
            this.opened = false
            if(this.record && this.project) this.opened = (this.project.active_phase_id === this.record.id)
            this.reroot_ids()
        },
        open_milestones_menu () {
            document.body.addEventListener("click", this.maybe_close_milestones_menu, { passive: false, capture: false })
            this.milestone_menu_opened = true
        },
        maybe_close_milestones_menu (event) {
            const container = event.target.closest(".uk-button-group")
            if(this.milestone_menu_opened && !container) this.close_milestones_menu()
        },
        close_milestones_menu () {
            this.milestone_menu_opened = false
            document.body.removeEventListener("click", this.maybe_close_milestones_menu, false)
        },
        toggle_milestones_menu () {
            if(this.milestone_menu_opened) this.close_milestones_menu()
            else this.open_milestones_menu()
        },
        milestone_act (action_id) {
            this.close_milestones_menu()
            switch (action_id) {
            case "add":
                {
                    // latest milestone start date + 1 day is new record's start
                    // end date is start + 1 week
                    let starts_at = null
                    this.milestones.forEach(milestone => {
                        const milestone_starts_at = this.$moment(milestone.starts_at)
                        if(!starts_at || milestone_starts_at.isBefore(starts_at)) starts_at = milestone_starts_at
                    })
                    const ends_at = starts_at ? this.$moment(starts_at).add(1, "weeks") : null
                    this.$store.dispatch(
                        "CREATE_RECORD",
                        {
                            entity: "task",
                            data: {
                                project_id: this.project.id,
                                phase_id: this.record.id,
                                milestone_id: null,
                                starts_at,
                                ends_at
                            }
                        }
                    ).then(record => {
                        this.ordered_ids.push(record.id)
                    })
                }
                break

            case "generate":
                this.tier_picking = true
                break
            }
        },
        sorted (event) {
            const milestones = orderBy(this.visible_milestones, Object.keys(this.manual_sort), Object.values(this.manual_sort))
            if(event.moved) { // re-order this phase's milestones
                milestones.splice(event.moved.newIndex, 0, milestones.splice(event.moved.oldIndex, 1)[0])
            } else if(event.added) { // moved task from another milestone to this phase
                milestones.map(milestone => milestone.id)
                milestones.splice(event.added.newIndex, 0, event.added.element)
            }
            if(event.moved || event.added) {
                const ids = []
                milestones.forEach((milestone, index) => {
                    milestone.project_id = this.record.project_id
                    milestone.phase_id = this.record.id
                    milestone.sort_order = index
                    ids.push(milestone.id)
                })
                this.ordered_ids = ids
                this.$store.dispatch("RECORD_ACTION", {
                    entity: this.entity,
                    id: this.record.id,
                    action: "sort",
                    data: {
                        ids
                    },
                    method: "post"
                }).then(() => {
                    this.reroot_ids()
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        },
        toggle_unstarred_team_members () {
            const pref = this.hide_unstarred_team_members_preference
            if(pref) {
                this.$store.dispatch("RECORD_DELETE", {
                    entity: "user_preference",
                    id: pref.id
                }).catch(error => {
                    this.$error(error.message)
                })
            } else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "user_preference",
                    data: {
                        user_id: this.profile_id,
                        entity: "project",
                        entity_id: this.project.id,
                        category: "team-members"
                    }
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        }
    },
    computed: {
        im_pic_or_pm () {
            if(!this.profile_id || !this.project) return false
            return (this.project.pic_id === this.profile_id) || (this.project.pm_id === this.profile_id)
        },
        comments () {
            if(!this.record || !this.record.id) return []
            return this.entity_records("comment").filter(comment => {
                return (comment.phase_id === this.record.id)
            })
        },
        milestones () {
            return this.tasks_by_phase_id(this.record.id).filter(task => {
                return !task.milestone_id
            })
        },
        visible_milestones () {
            /*
            let rows
            if(!this.frozen_row_state || !this.frozen_row_ids) {
                rows = orderBy(
                    this.milestones.filter(task => {
                        return (!task.completed_at || (this.show_completed && !!task.completed_at))
                    }
                ), Object.keys(this.manual_sort), Object.values(this.manual_sort))
                this.frozen_row_ids = rows.map(row => row.id)
            } else {
                rows = this.frozen_row_ids.reduce((rows, row_id) => {
                    const row = this.milestones.find(row => row.id === row_id)
                    if(row) rows.push(row)
                    return rows
                }, [])
            }
            */
            const rows = this.ordered_ids.reduce((rows, row_id) => {
                const row = this.milestones.find(row => row.id === row_id)
                if(row) rows.push(row)
                return rows
            }, [])
            return rows
        },
        completed_milestones () {
            const completed_milestones = this.milestones.find(milestone => {
                return !!milestone.completed_at
            })
            return !!completed_milestones
        },
        milestone_actions () {
            return [
                { id: "generate", name: "Auto-generate milestones" }
            ]
        },
        hide_unstarred_team_members_preference () {
            return this.entity_records("user_preference").find(user_preference => {
                return (user_preference.user_id === this.profile_id) && (user_preference.category === "team-members") && (user_preference.entity === "project") && (user_preference.entity_id === this.project.id)
            })
        },
        hide_unstarred_team_members () {
            return !!this.hide_unstarred_team_members_preference
        }
    },
    props: {
        record: {
            type: Object,
            required: true
        },
        project: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        },
        dragging: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            entity: "phase",
            frozen_row_state: false,
            frozen_row_ids: null,
            ordered_ids: [],
            manual_sort: { sort_order: "asc", proxied_ends_at: "asc", id: "asc" },
            milestone_menu_opened: false,
            show_completed: false,
            tier_picking: false,
            opened: false
        }
    }
}
</script>
